import { times } from '@sama/micro-dash';
import { round } from 'lodash';
import { Vector3 } from 'three';
import { pointToVector, vectorToPoint } from '../../3d/utils/point-cloud-utils';
import {
  Point2D,
  Point3D,
  PointType,
} from '../../team-apis/otterdam/answers/shape-typings';

// 2D

export function eventToPoint2D(event: MouseEvent): Point2D {
  return [event.clientX, event.clientY];
}

// 3D

export function roundVectors(points: Vector3[]) {
  return points.map(roundVector);
}

export function roundVector(vector: Vector3): Point3D {
  return [roundValue(vector.x), roundValue(vector.y), roundValue(vector.z)];
}

export function roundValue(value: number) {
  return round(value, 5);
}

export function mapVector(v: Vector3, fn: (value: number) => number) {
  return pointToVector(mapPoint(vectorToPoint(v), fn));
}

// General

export function addPoints<P extends PointType>(p1: P, p2: P): P {
  return reducePoints([p1, p2], (v1, v2) => v1 + v2);
}

export function subtractPoints<P extends PointType>(p1: P, p2: P): P {
  return reducePoints([p1, p2], (v1, v2) => v1 - v2);
}

export function multiplyPoints<P extends PointType>(p1: P, p2: P): P {
  return reducePoints([p1, p2], (v1, v2) => v1 * v2);
}

export function dividePoints<P extends PointType>(p1: P, p2: P): P {
  return reducePoints([p1, p2], (v1, v2) => v1 / v2);
}

export function distanceBetween<P extends PointType>(p1: P, p2: P) {
  return Math.hypot(...subtractPoints(p1, p2));
}

export function midPoint<P extends PointType>(points: P[]): P {
  const max = reducePoints(points, Math.max);
  const min = reducePoints(points, Math.min);
  return scalePoint(addPoints(max, min), 0.5);
}

export function scalePoint<P extends PointType>(p: P, scale: number): P {
  return mapPoint(p, (v) => v * scale);
}

export function reducePoints<P extends PointType>(
  points: P[],
  reduceCoordinates: (v1: number, v2: number) => number,
) {
  const reduceTwoPoints = (p1: P, p2: P) =>
    times(p1.length, (i) => reduceCoordinates(p1[i], p2[i])) as P;
  return points.reduce(reduceTwoPoints);
}

export function mapPoint<P extends PointType>(
  p: P,
  fn: (value: number) => number,
) {
  return p.map(fn) as P;
}

export function dotProduct<P extends PointType>(p1: P, p2: P) {
  return times(p1.length, (i) => p1[i] * p2[i]).reduce((a, b) => a + b);
}
